"use client";

import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "@/app/hooks";
import { cn } from "@/app/lib/utils";
import { useStateHistoryChatPage } from "@/app/store/chat";
import { ChatHistory } from "./ChatHistory";
import { ChatSearchDialog } from "./ChatSearchDialog";

type ChatSidebarLayoutProps = {
   children: ReactNode;
   className?: string;
};

export function ChatSidebarLayout({ children, className }: ChatSidebarLayoutProps) {
   const [isSidebarResizing, setIsSidebarResizing] = useState(false);
   const [viewportWidth, setViewportWidth] = useState(0);
   const {
      initModel,
      setHistoryItem,
      setTemporaryChat,
      isOpenHistoryChat,
      setOpenHistoryChat,
      setSearchDialogOpen,
      chatHistorySidebarWidth,
      setChatHistorySidebarWidth
   } = useStateHistoryChatPage();

   const isMobile = useMediaQuery("(max-width: 768px)");
   const isTablet = useMediaQuery("(min-width: 769px) and (max-width: 1024px)");
   const isCompactLayout = isMobile || isTablet;

   const effectiveSidebarWidth = useMemo(() => {
      if (isCompactLayout || viewportWidth <= 0) return chatHistorySidebarWidth;
      return Math.min(chatHistorySidebarWidth, Math.max(240, viewportWidth - 80));
   }, [chatHistorySidebarWidth, isCompactLayout, viewportWidth]);

   useEffect(() => {
      setHistoryItem(null);
      setTemporaryChat(false);
      initModel();
   }, [initModel, setHistoryItem, setTemporaryChat]);

   useEffect(() => {
      const syncViewportWidth = () => {
         setViewportWidth(window.innerWidth);
      };

      syncViewportWidth();
      window.addEventListener("resize", syncViewportWidth);

      return () => {
         window.removeEventListener("resize", syncViewportWidth);
      };
   }, []);

   useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
         const isSearchShortcut = (event.metaKey || event.ctrlKey) && event.key === "k";
         if (isSearchShortcut) {
            event.preventDefault();
            setSearchDialogOpen(true);
         }
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => {
         window.removeEventListener("keydown", handleKeyDown);
      };
   }, [setSearchDialogOpen]);

   const desktopMarginLeft =
      isOpenHistoryChat && !isCompactLayout ? effectiveSidebarWidth : 64;

   return (
      <>
         <ChatHistory
            showBrandHeader
            isOpen={isOpenHistoryChat}
            onOpenChange={setOpenHistoryChat}
            sidebarWidth={effectiveSidebarWidth}
            onSidebarWidthChange={setChatHistorySidebarWidth}
            isResizing={isSidebarResizing}
            onResizeStateChange={setIsSidebarResizing}
            className="z-30 fixed"
         />
         <main
            className={cn(
               "min-h-screen bg-background",
               !isSidebarResizing && "transition-all duration-300",
               className
            )}
            style={{ marginLeft: desktopMarginLeft }}
         >
            {children}
         </main>
         <ChatSearchDialog />
      </>
   );
}
